import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { CsPage, NavController } from 'providers/nav-controller';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import {
  UserDb,
  currentPlanId,
  declaredStateId,
  fitnessStateId,
  immutableStateId,
} from 'services/user-db.service';
import { AuthService } from 'services/auth.service';
import { DatabaseWrapper, DocWatcher } from 'services/database-wrapper';
import { CoachClientSlot, CoachClientStatus, CoachRelationship, coachRelAsClientId, isSlotSleeping } from '@cf-shared/data-model/coach';
import {
  computeClientsToPause,
  computeCoachSlotAvailability,
  describeAsleepDuration,
} from 'util/coach-sleep-display';
import { CoachTierService } from 'services/coach-tier.service';
import { describeQueuedTierChangeDate } from './queued-tier-change-date';
import { describeError } from '@cf-shared/error-describe';
import { BadgeColor } from 'components/cs-badge/cs-badge';
import { CoachSettingsPageParams } from './coach-settings-interface';
import { ICoachClientDetailPage } from 'pages/coach-client-detail/coach-client-detail-interface';
import { ICoachBillingPage } from 'pages/coach-billing/coach-billing-page-interface';
import { ICoachBrandingPage } from 'pages/coach-branding/coach-branding-interface';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { CoachPresenceService, PresenceState } from 'services/coach-presence.service';
import { StillClaimedCheck, isClientStillClaimed } from 'services/coach-presence-helpers';
import { InitialSyncWaitFor, waitForInitialSyncAsync } from 'services/user-db.task-status';
import { PubSubService, PubSubRegistration } from 'services/pubsub.service';
import { ConnectionState, connectionStateChangedEvent } from 'services/online-state.service';
import { ClientDocsRead, CoachClientDataService } from 'services/coach-client-data.service';
import { CoachDemoSeedService } from 'services/coach-demo-seed.service';
import { LoggingService } from 'services/logging.service';
import { CoachClientEntryService } from 'services/coach-client-entry.service';
import { ConfigDbService } from 'services/config-db.service';
import { isCoachingDashboardOnAsync, watchCoachingDashboard } from 'services/coaching-dashboard-flag';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { isTrainingOverviewEntryShown } from './training-overview-entry';
import { isLogRecordEntry, Log_Entry } from '@cf-shared/data-model/user-log';
import { ImmutableState, DeclaredState, FitnessState } from '@cf-shared/data-model/user-state';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { DateTime } from 'luxon';
import {
  ClientDashboardStatus,
  CoachDashboardRow,
  HandoverState,
  buildDashboardRow,
  checkClientHandover,
  coachClientLogReadRangeUtc,
  sortDashboardRows,
} from 'services/coach-dashboard-helpers';

/** A slot this page reads remote data for: claimed, with a client, and not asleep. */
function isReadableClaim(slot: CoachClientSlot): boolean {
  return slot.status === CoachClientStatus.Claimed && !!slot.clientUid && !isSlotSleeping(slot);
}

/**
 * Show a nickname prompt for creating or renaming a client slot.
 * Returns the trimmed nickname, or null if cancelled or duplicate.
 * Exported for reuse in CoachClientDetailPage (rename flow).
 */
export async function promptCoachNicknameAsync(
  alertCtrl: AlertController,
  existingSlots: CoachClientSlot[],
  opts: { mode: 'create' | 'rename'; currentValue?: string },
): Promise<string | null> {
  const isCreate = opts.mode === 'create';
  return alertCtrl.showAlertAsync<string | null>({
    title: isCreate
      ? LL.coach.nicknamePrompt.createTitle()
      : LL.coach.nicknamePrompt.renameTitle(),
    message: LL.coach.nicknamePrompt.message(),
    inputs: [{
      type: 'text',
      name: 'nickname',
      placeholder: LL.coach.nicknamePrompt.placeholder(),
      value: opts.currentValue || '',
    }],
    buttons: [
      { text: LL.coach.nicknamePrompt.cancel(), role: AlertButtonRole.Cancel, result: null },
      {
        text: isCreate
          ? LL.coach.nicknamePrompt.create()
          : LL.coach.nicknamePrompt.rename(),
        role: AlertButtonRole.Primary,
        result: (data: any) => {
          const name = (data.nickname || '').trim();
          if (!name) return null;
          // case-insensitive duplicate check, skip self for rename
          const isDuplicate = existingSlots.some(s =>
            s.nickname.toLowerCase() === name.toLowerCase()
            && s.nickname.toLowerCase() !== (opts.currentValue || '').toLowerCase()
          );
          if (isDuplicate) return null;
          return name;
        },
      },
    ],
  });
}

/** Singleton ids consumed by the Goal-card solver — the same four the user's own
 *  app loads, imported from where it defines them rather than typed out again,
 *  plus the client's coach link, which `checkClientHandover` reads to tell a
 *  finished handover from a broken one (#4817). They travel in ONE `_all_docs`
 *  POST, so the extra id costs no extra request. */
const SINGLETON_IDS = [
  immutableStateId,
  declaredStateId,
  fitnessStateId,
  currentPlanId,
  coachRelAsClientId,
];

/** Status → badge color + label lookup tables. Centralised so adding a new
 *  `ClientDashboardStatus` enum value forces an entry here (the
 *  `Record<…, …>` type makes the omission a compile error). */
const STATUS_BADGE_COLOR: Record<ClientDashboardStatus | 'Unknown', BadgeColor> = {
  [ClientDashboardStatus.OnTrack]: BadgeColor.Green,
  [ClientDashboardStatus.OffTrack]: BadgeColor.Yellow,
  [ClientDashboardStatus.Undetermined]: BadgeColor.Gray,
  Unknown: BadgeColor.Gray,
};
const STATUS_LABEL_FN: Record<ClientDashboardStatus | 'Unknown', () => TranslatedString> = {
  [ClientDashboardStatus.OnTrack]: () => LL.coach.dashboard.statusOnTrack(),
  [ClientDashboardStatus.OffTrack]: () => LL.coach.dashboard.statusOffTrack(),
  [ClientDashboardStatus.Undetermined]: () => LL.coach.dashboard.statusUndetermined(),
  Unknown: () => LL.coach.dashboard.statusUnknown(),
};

/**
 * Coach client list page. Shows all coach-client/* slots as a dashboard
 * table for claimed clients (Client · Status · Pending action items) +
 * a pending-invitations section below. Status mirrors the home Goal-card
 * bucket via the existing pure solver; action items are detected from
 * the client's logs (weekly checkup awaiting coach review, unreviewed
 * strength-test workouts).
 *
 * The docWatcher re-queries the slot list on any LOCAL document change.
 * The cross-client dashboard data is fetched authenticated-remote on
 * `ionViewWillEnter` and on the explicit refresh-button tap (the heavy
 * status fetch is deliberately NOT polled — the existing 60s presence
 * poll is unchanged).
 */
@Component({
  selector: 'page-coach-settings',
  templateUrl: 'coach-settings.html',
  host: { 'class': 'coach-settings' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoachSettingsPage extends CsPage<CoachSettingsPageParams> {
  protected LL = LL;
  clientSlots: CoachClientSlot[] = [];
  maxSlots = 0;
  /** Presence state per claimed client, keyed by clientUid. */
  presenceByClientUid = new Map<string, PresenceState>();

  /** #3991: re-read presence as soon as connectivity returns (see ionViewWillEnter). */
  private connectionSub: PubSubRegistration;
  /** Dashboard row per claimed client (status + action items). Absence = still loading. */
  dashboardByClientUid = new Map<string, CoachDashboardRow>();
  /** True while a refresh-all is in flight (drives the refresh-button spinner). */
  isRefreshing = false;
  private docWatcher: DocWatcher;
  /** Single in-flight promise serializing docWatcher-driven slot-change handling
   *  (see `handleSlotsChangedAsync`); collapses concurrent callbacks into at most
   *  one queued rerun. */
  private slotsChangeInFlight: Promise<void> | null = null;
  private slotsChangeQueued = false;
  /** One shared wait for the user DB's initial sync per page entry; see `stillClaimedCheck`. */
  private initialSyncSettledWait: Promise<unknown> | null = null;
  /** the coaching dashboard switch (#5456), for the signed-in account, which on this page is the
   *  coach; it decides whether a client card offers the Training Overview (#6795) */
  private coachingDashboardOn = false;
  private unwatchCoachingDashboard?: () => void;

  constructor(
    private navCtrl: NavController,
    private alertCtrl: AlertController,
    private userDb: UserDb,
    private auth: AuthService,
    private coachPresence: CoachPresenceService,
    private coachClientData: CoachClientDataService,
    private coachTier: CoachTierService,
    private demoSeed: CoachDemoSeedService,
    private logging: LoggingService,
    private cd: ChangeDetectorRef,
    private pubSub: PubSubService,
    private configDb: ConfigDbService,
    private clientEntry: CoachClientEntryService,
  ) {
    super();
  }

  async ionViewWillEnter() {
    // reset transient flags on every enter — a prior session that destroyed the
    // view mid-fetch could otherwise leave `isRefreshing` stuck true and silently
    // disable the manual refresh button.
    this.isRefreshing = false;
    // a fresh entry may follow a database switch, so the sync wait is not carried over
    this.initialSyncSettledWait = null;
    // ionViewWillEnter runs again on every entry, on the SAME page instance (cs-nav
    // clears `viewDestroyed` and re-enters it), so drop the previous watcher before
    // making a new one — same reason the connection subscription below is dropped
    // first. Without this, every return to the client list leaves the previous
    // watcher registered and firing: one document change then starts one reload
    // cascade per visit the page has ever had.
    this.docWatcher?.destroy();
    this.docWatcher = this.userDb.database.createDocWatcher(() => {
      if (this.viewDestroyed) return;
      void this.handleSlotsChangedAsync();
    });
    this.maxSlots = this.auth.getEffectiveCoachSlots();
    // watch before the first read: registered after an await, a page destroyed meanwhile would
    // leak the watcher, and a change landing between the read and the watch would be missed
    if (!this.unwatchCoachingDashboard) {
      this.unwatchCoachingDashboard = watchCoachingDashboard(this.configDb, isOn => {
        if (this.viewDestroyed || isOn === this.coachingDashboardOn) return;
        this.coachingDashboardOn = isOn;
        this.cd.markForCheck();
      });
    }
    this.coachingDashboardOn = await isCoachingDashboardOnAsync(this.configDb);
    await this.loadClientSlots();
    void this.refreshQueuedDowngradeNoticeAsync();
    await this.refreshPresenceAsync();
    void this.refreshDashboardAsync();
    this.schedulePresenceRefresh();

    // #3991: refresh presence the moment we come back online.
    //
    // Presence was refreshed by the 60 s poll and by page init, and by nothing else — this page
    // listened for no connectivity signal at all. So after a reconnect a coach could sit looking at
    // stale grey dots for up to a minute, on the one page whose entire job is telling them who is
    // online. (It looked permanent in practice, because tapping a client card re-inits the page and
    // silently fixes it, so you never wait long enough to see the poll do it.)
    //
    // Both directions matter, and they are handled differently:
    //
    //  - back ONLINE: re-read presence at once, rather than making the coach wait up to a minute for
    //    the next poll while the dots sit grey.
    //
    //  - gone OFFLINE: we cannot know who is online any more, so stop claiming anyone is. Done
    //    locally — firing a burst of HTTP GETs we know will fail would grey the dots too (each
    //    failed read returns online:false), but only after the requests time out, and it would throw
    //    away the last-seen times in the process.
    //
    // ionViewWillEnter runs again on every entry (coming back from a client card, for instance), so
    // drop any previous subscription first — otherwise they stack up and each transition fires once
    // per visit the page has ever had.
    this.connectionSub?.unsubscribe();
    this.connectionSub = this.pubSub.subscribe(connectionStateChangedEvent, (state: ConnectionState) => {
      if (this.viewDestroyed) return;
      if (state === ConnectionState.Online) {
        void this.refreshPresenceAsync();
      } else {
        this.markPresenceUnknownWhileOffline();
      }
    });
  }

  /** #3991: offline, we have no way to know who is currently online — the presence docs live on the
   *  server. So stop showing anyone as online.
   *
   *  The last-seen TIME is kept, because it is still true and still useful: "last seen 5m ago" is an
   *  honest thing to say offline, whereas a green "Online" dot is not. (This is also why we do not
   *  simply let the failing poll do it: a failed read returns online:false with NO lastSeenUtc, so
   *  it would silently throw the last-seen labels away.) */
  private markPresenceUnknownWhileOffline() {
    if (this.presenceByClientUid.size === 0) return;
    const next = new Map<string, PresenceState>();
    for (const [uid, state] of this.presenceByClientUid) {
      next.set(uid, { ...state, online: false });
    }
    this.presenceByClientUid = next;
    this.cd.markForCheck();
  }

  /** Serialize docWatcher-driven slot-change handling so concurrent callbacks
   *  (e.g. a burst of writes during a slot-claim flow) cannot race the
   *  copy-then-assign sequence on `dashboardByClientUid` / `presenceByClientUid`.
   *  At most one rerun is queued — additional concurrent fires collapse into
   *  the queued run since they all need the same "read latest, fan out new". */
  private handleSlotsChangedAsync(): Promise<void> {
    if (this.slotsChangeInFlight) {
      this.slotsChangeQueued = true;
      return this.slotsChangeInFlight;
    }
    this.slotsChangeInFlight = (async () => {
      try {
        do {
          this.slotsChangeQueued = false;
          await this.handleSlotsChangedOnceAsync();
        } while (this.slotsChangeQueued && !this.viewDestroyed);
      } finally {
        this.slotsChangeInFlight = null;
      }
    })();
    return this.slotsChangeInFlight;
  }

  /** Single-pass slot-change handler: reload slots, then fan out per-client
   *  fetches for any newly-claimed uids. Wrapped by `handleSlotsChangedAsync`
   *  which serializes concurrent invocations. */
  private async handleSlotsChangedOnceAsync(): Promise<void> {
    const previouslyClaimed = new Set(this.claimedClientUids());
    await this.loadClientSlots();
    if (this.viewDestroyed) return;
    // The queued-downgrade notice names specific clients, so it has to be
    // recomputed whenever the client list moves under it (#4454) — otherwise a
    // coach who parks or removes somebody keeps reading a notice that names
    // the previous set. Before the early return below, because that return
    // fires on the common "nothing newly claimed" path.
    void this.refreshQueuedDowngradeNoticeAsync();
    const newlyClaimed = this.claimedClientUids().filter(uid => !previouslyClaimed.has(uid));
    if (newlyClaimed.length === 0) return;
    // fetch dashboard rows + presence in parallel for the new uids; both awaits
    // keep their writes inside the serialized envelope so a queued rerun starts
    // after this iteration finishes, not while writes are still landing
    const [, presenceResults] = await Promise.all([
      Promise.all(newlyClaimed.map(uid => this.computeRowAsync(uid))),
      Promise.all(newlyClaimed.map(async uid =>
        [uid, await this.readPresenceOrSyntheticAsync(uid)] as const)),
    ]);
    if (this.viewDestroyed) return;
    // rebuild the presence map ONCE so a burst of newly-claimed slots costs one
    // map copy, not N² (dashboard map updates already happen inside computeRowAsync)
    const nextPresence = new Map(this.presenceByClientUid);
    for (const [uid, state] of presenceResults) nextPresence.set(uid, state);
    this.presenceByClientUid = nextPresence;
    this.cd.markForCheck();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
    this.connectionSub?.unsubscribe();
    this.unwatchCoachingDashboard?.();
    // safeTimeout handles cancel-on-destroy for the poll timer automatically
  }

  private async loadClientSlots() {
    const docs = await this.userDb.database.getByIdHeadAsync('coach-client/', this.docWatcher) as CoachClientSlot[];
    this.clientSlots = (docs || []).sort((a, b) => a.createdAt < b.createdAt ? -1 : 1);
    this.cd.markForCheck();
  }

  /** Read presence for a single client, short-circuiting through the
   *  demo seed for synthetic uids (TestUI "Become a demo coach") so
   *  the test does not hit the remote presence backend. */
  private readPresenceOrSyntheticAsync(uid: string): Promise<PresenceState> {
    const synth = this.demoSeed.getSyntheticPresence(uid);
    if (synth) return Promise.resolve(synth);
    return this.coachPresence.readPresenceOnce(uid, this.stillClaimedCheck(uid));
  }

  /**
   * df-ad81a785446c: the check the remote reads ask before reporting a 401/403.
   * On a warm resume this page reads the slots from the LOCAL database before its
   * initial sync has run, so a client the coach removed days ago can still be listed,
   * and the server rightly refuses to answer for them. So wait for that sync to
   * settle, re-read the slots, and say whether this client still holds a claimed
   * slot (asleep or not; see `isClientStillClaimed`). If the wait gives up at its
   * cap, the local slots are judged as they are, which errs toward reporting.
   */
  private stillClaimedCheck(clientUid: string): StillClaimedCheck {
    return async () => {
      const database = this.userDb.database;
      this.initialSyncSettledWait ??= waitForInitialSyncAsync(
        listener => database.onInitialSyncSettled(listener),
        InitialSyncWaitFor.AnyOutcome,
      );
      await this.initialSyncSettledWait;
      const docs = await database.withDocWatcherAsync(
        watcher => database.getByIdHeadAsync('coach-client/', watcher)) as CoachClientSlot[];
      return isClientStillClaimed(docs, clientUid);
    };
  }

  /** Fan-out one authenticated remote GET per claimed slot and cache the results. */
  private async refreshPresenceAsync() {
    if (this.viewDestroyed) return;
    const claimedUids = this.claimedClientUids();
    // fetch all in parallel — one remote GET per claimed slot
    const results = await Promise.all(
      claimedUids.map(async uid => [uid, await this.readPresenceOrSyntheticAsync(uid)] as const),
    );
    if (this.viewDestroyed) return;
    const next = new Map<string, PresenceState>();
    for (const [uid, state] of results) next.set(uid, state);
    this.presenceByClientUid = next;
    this.cd.markForCheck();
  }

  /** Fan-out dashboard fetches per claimed client; each row populates as it resolves
   *  (skeleton placeholder while undefined in `dashboardByClientUid`). */
  private async refreshDashboardAsync() {
    if (this.viewDestroyed) return;
    const claimedUids = this.claimedClientUids();
    this.isRefreshing = true;
    // clear stale rows so the table shows skeletons while a fresh fetch is in flight
    this.dashboardByClientUid = new Map();
    this.cd.markForCheck();
    try {
      // fan out — each row's awaits complete independently
      await Promise.all(claimedUids.map(uid => this.computeRowAsync(uid)));
    } finally {
      // belt-and-suspenders reset; `ionViewWillEnter` also resets the flag at
      // entry so a destroy-mid-fetch on one instance can't poison the next.
      this.isRefreshing = false;
      this.cd.markForCheck();
    }
  }

  private async computeRowAsync(clientUid: string): Promise<void> {
    if (this.viewDestroyed) return;
    // Demo-seed short-circuit (TestUI "Become a demo coach"). Synthetic
    // uids return canned rows without touching CouchDB. The "stuck
    // loading" marker keeps the slot in skeleton state by leaving the
    // map entry absent — the page's `isDashboardLoading(slot)` reads
    // map presence to decide. We return without waiting so the outer
    // `refreshDashboardAsync` Promise.all settles and the refresh
    // spinner stops.
    if (this.demoSeed.isSynthetic(clientUid)) {
      const seeded = this.demoSeed.getSyntheticRow(clientUid);
      if (seeded && !this.demoSeed.isSyntheticLoading(clientUid)) {
        const nextSeed = new Map(this.dashboardByClientUid);
        nextSeed.set(clientUid, seeded);
        this.dashboardByClientUid = nextSeed;
        this.cd.markForCheck();
      }
      return;
    }
    let row: CoachDashboardRow;
    try {
      // fetch singletons + 30-day log range in parallel — ~2 requests per client
      const [singletonRead, logs] = await Promise.all([
        this.coachClientData.readClientDocsAsync(clientUid, SINGLETON_IDS, this.stillClaimedCheck(clientUid)),
        this.fetchClientLogsAsync(clientUid),
      ]);
      row = this.buildRow(clientUid, singletonRead, logs);
    } catch (err) {
      // unexpected helper failure — surface to telemetry per CLAUDE.md
      // "unexpected conditions must surface". Returns Unknown so the row
      // doesn't disappear; the trail goes to Sentry, not just dev console.
      this.logging.trackAbnormalException(err);
      // NotChecked, not Missing: we never looked, so we may not report this
      // client's handover as broken (#4817).
      row = { clientUid, status: 'Unknown', items: [], handover: HandoverState.NotChecked };
    }
    if (this.viewDestroyed) return;
    // immutable map update so OnPush re-renders even when the reference set is small
    const next = new Map(this.dashboardByClientUid);
    next.set(clientUid, row);
    this.dashboardByClientUid = next;
    this.cd.markForCheck();
  }

  private async fetchClientLogsAsync(clientUid: string): Promise<Log_Entry[]> {
    // 30-day window, see `coachClientLogReadRangeUtc` for where it ends. The narrower subset the plan check
    // is given is filtered out of this set by `buildDashboardRow`.
    const { startUtc, endUtc } = coachClientLogReadRangeUtc(this.userDb.debug.getNowLocal());
    const docs = await this.coachClientData.readClientLogRangeAsync<Log_Entry>(
      clientUid, startUtc, endUtc, this.stillClaimedCheck(clientUid));
    // readClientLogRangeAsync returns every doc under the `log/` id prefix, and that
    // prefix is also shared by non-entry journal snapshots — FitnessPlan plan backups
    // (`log/<ts>-FitnessPlan`) and IntakeForm snapshots — that are not log entries (no
    // `type`). isLogRecordEntry drops them by `type`. A plan backup also has no time_utc,
    // so without this filter the row builder mistakes it for a log entry with a broken
    // time; its invalid-time_utc report then only fires for a genuine entry.
    return docs.filter(isLogRecordEntry);
  }

  /**
   * Hand the fetched documents to the pure row builder.
   *
   * Everything that needs the component stays here — the coach's local now, the
   * handover check (which reads this coach's slot list and identity), and the two
   * telemetry calls. The deciding itself lives in `coach-dashboard-helpers.ts`,
   * beside the other pure dashboard rules, where it can be unit-tested.
   */
  private buildRow(clientUid: string, singletonRead: ClientDocsRead<any>, logs: Log_Entry[]): CoachDashboardRow {
    const singletons = singletonRead.docs;
    // keep this its own statement — it writes a Sentry report, and it must run
    // before the row builder's own reporting
    const handover = this.checkHandoverAndReport(clientUid, singletonRead);
    return buildDashboardRow({
      clientUid,
      solverInputs: {
        immutable: singletons.get(immutableStateId) as ImmutableState | undefined,
        declared: singletons.get(declaredStateId) as DeclaredState | undefined,
        fitness: singletons.get(fitnessStateId) as FitnessState | undefined,
        plan: singletons.get(currentPlanId) as FitnessPlan | undefined,
      },
      logs,
      todayLocal: this.userDb.debug.getNowLocal(),
      handover,
      reporter: {
        reportProblem: (name, context) => this.logging.trackSilentProblem(name, context),
        reportException: err => this.logging.trackAbnormalException(err),
      },
    });
  }

  /**
   * Ask whether this claimed client's account shows the coach's handover
   * finishing, and report it when it does not (#4817).
   *
   * The reporting lives here rather than in the pure helper so the helper stays
   * testable, and it uses `trackSilentProblem` rather than `trackSilentInfo`
   * because nothing has repaired anything: the client's account really is
   * missing what the claim was supposed to put there, and it stays missing until
   * a person acts. This is also the ONLY measurement of how often a claim loses
   * its handover — the condition has no telemetry anywhere else today.
   */
  private checkHandoverAndReport(clientUid: string, singletonRead: ClientDocsRead<any>): HandoverState {
    const slot = this.clientSlots.find(s => s.clientUid === clientUid);
    if (!slot) return HandoverState.NotChecked;
    const handover = checkClientHandover({
      coachUid: this.auth.user?.uid || '',
      slot,
      clientCoachRel: singletonRead.docs.get(coachRelAsClientId) as CoachRelationship | undefined,
      couldReadClientAccount: singletonRead.ok,
      now: this.userDb.debug.getNowLocal().toUTC(),
    });
    if (handover === HandoverState.Missing) {
      this.logging.trackSilentProblem('CoachClaim: claimed client has no completed handover', {
        clientUid,
        slotId: slot._id,
        claimedAt_utc: slot.claimedAt_utc,
      });
    }
    return handover;
  }

  /**
   * The clients this page fetches live data for — presence, and the dashboard
   * status and action-item rows.
   *
   * PARKED CLIENTS ARE EXCLUDED (#4530), and that is about more than saving
   * requests. The screen already hides a parked client's presence dot, "last
   * seen" line and on-track status, because none of them describe coaching that
   * is currently happening. Fetching the data anyway and then hiding it would
   * mean the coach's app kept reading a parked client's own records — every 60
   * seconds for presence, for as long as this page is open — which is the exact
   * thing parking is meant to stop. The saving is real too: one remote request
   * per parked client per poll, plus a read over their log history.
   */
  private claimedClientUids(): string[] {
    return this.clientSlots.filter(isReadableClaim).map(s => s.clientUid);
  }

  /** Poll presence every 60s while the page is live. Uses `safeTimeout` so the callback is silently skipped after `ionViewDidLeave` / `ngOnDestroy`. */
  private schedulePresenceRefresh() {
    this.safeTimeout(async () => {
      if (this.viewDestroyed) return;
      await this.refreshPresenceAsync();
      if (this.viewDestroyed) return;
      this.schedulePresenceRefresh();
    }, 60_000);
  }

  /** Returns the cached presence state for a slot, or undefined if unknown / slot not claimed. */
  getPresenceState(slot: CoachClientSlot): PresenceState | undefined {
    if (!slot.clientUid) return undefined;
    return this.presenceByClientUid.get(slot.clientUid);
  }

  /** Whether a slot's client was seen within the 2-minute staleness window. */
  isClientOnline(slot: CoachClientSlot): boolean {
    return !!this.getPresenceState(slot)?.online;
  }

  /** Whether to render the presence dot + label at all — only for Claimed slots we have data for. */
  hasPresenceData(slot: CoachClientSlot): boolean {
    return slot.status === CoachClientStatus.Claimed && this.presenceByClientUid.has(slot.clientUid || '');
  }

  /** Human-readable "Online" / "Last seen 5m ago" text for the slot card. */
  getPresenceLabel(slot: CoachClientSlot): string {
    const state = this.getPresenceState(slot);
    if (!state) return '';
    if (state.online) return LL.coach.presence.online();
    if (!state.lastSeenUtc) return '';
    return LL.coach.presence.lastSeen({ time: this.coachPresence.getTimeSinceString(state.lastSeenUtc) });
  }

  /** True for a Paddle per-seat (web) coach — they are uncapped (pay-per-seat),
   *  so the numeric slot cap does not apply and the UI shows "unlimited". Read
   *  live so it survives the transient post-reload window where auth is still
   *  loading (same reason as `canUseBranding`). */
  get isSeatCoach(): boolean {
    return this.auth.getHasCoachSeat();
  }

  /** Free places, at-capacity, and the used count — the SAME rule the server
   *  enforces, so the app and the server cannot disagree about whether the
   *  coach has room. Lives in a pure helper so it is testable; see
   *  `computeCoachSlotAvailability`. */
  private get slotAvailability(): { used: number; remaining: number; atCapacity: boolean } {
    return computeCoachSlotAvailability(this.clientSlots, this.maxSlots);
  }

  get slotsAtCapacity(): boolean {
    // a Paddle seat coach is never at capacity — pay-per-seat has no numeric cap
    // (and `maxSlots` is 0 for them, which would otherwise read as always-full).
    if (this.isSeatCoach) return false;
    return this.slotAvailability.atCapacity;
  }

  /** The "N client slots remaining" line. A Paddle seat coach is uncapped and
   *  billed per seat, so this shows "Add clients anytime — billed per seat"
   *  instead of a (meaningless) number. */
  slotsRemainingText(): TranslatedString {
    if (this.isSeatCoach) return LL.coach.perSeatSlots();
    return LL.coach.slotsRemaining({ remaining: this.slotAvailability.remaining });
  }

  /** Badge colour for a parked client — deliberately the neutral one. Parking
   *  by hand is an ordinary choice the coach made, and an automatic parking
   *  (#4454) is explained by the row text rather than flagged by colour. */
  readonly asleepBadgeColor = BadgeColor.Gray;

  /** Whether this client is parked — by the coach (#4530) or automatically,
   *  because the coach's plan is full (#4454). The row text says which. */
  isSlotAsleep(slot: CoachClientSlot): boolean {
    return isSlotSleeping(slot);
  }

  /** "Asleep since today" / "Asleep for 12 days", or — since #4454 — "Paused
   *  for 3 days because your plan is full" when the server did the parking.
   *  Only read for a parked slot. */
  asleepForText(slot: CoachClientSlot): TranslatedString {
    return describeAsleepDuration(slot, DateTime.now());
  }

  /** The standing "clients will be paused at renewal" notice, or null (#4454). */
  queuedPauseNotice: { count: number; names: string; cap: number; date?: string } | null = null;

  /**
   * Work out whether to show the queued-downgrade notice, and what it says.
   *
   * Only Apple defers a plan change to the next renewal, so only an Apple coach
   * ever sees this; Google Play applies the change at once, and those clients
   * are already parked before the coach gets back to this screen.
   *
   * Best-effort throughout. If the catalogue read fails, or the queued plan
   * cannot be resolved to a client limit, the notice simply does not appear —
   * see `computeClientsToPause` for why saying nothing beats guessing.
   *
   * Re-run whenever the client list changes, not only on entry: the coach can
   * park, wake, add or lose a client while sitting on this page, and a notice
   * left naming the earlier set is the exact "names different people from the
   * ones who lose access" failure the shared choosing rule exists to prevent.
   * The catalogue is cached for the session, so re-running is not a repeated
   * network call.
   */
  private async refreshQueuedDowngradeNoticeAsync(): Promise<void> {
    const tier = this.auth.couch?.coachTierData;
    const queuedProductId = tier?.autoRenewProductId;
    // No change is queued when the coach will renew onto the plan they are
    // already on — the field carries the current product in that case.
    if (!tier || !queuedProductId || queuedProductId === tier.productId) {
      this.setQueuedPauseNotice(null);
      return;
    }

    if (this.queuedTierCap?.productId !== queuedProductId) {
      try {
        // The coach's record carries the queued plan's IDENTIFIER and nothing
        // else; turning that into a client limit needs the catalogue, which
        // lives server-side. Never hard-code the mapping.
        const options = await this.coachTier.loadTiersAsync();
        const cap = options.find((option) =>
          option.entry.appleProductId === queuedProductId
          || option.entry.googlePlayProductId === queuedProductId)?.entry.cap;
        this.queuedTierCap = { productId: queuedProductId, cap };
      } catch (err) {
        this.logging.trackSilentProblem('coach-queued-downgrade-catalog-read-failed', {
          message: describeError(err),
        });
        // Clear the notice before giving up. We only reach this read when the
        // queued plan has CHANGED, which is exactly when a notice built from
        // the previous one may still be on screen — and that notice would name
        // the wrong clients, the wrong limit and the wrong date. Showing
        // nothing is the right failure here; showing the old thing is not.
        this.setQueuedPauseNotice(null);
        // Deliberately NOT cached as a failure. A later refresh retries, which
        // is how the notice recovers from a network blip while the coach sits
        // on this page. The cost is one repeated report per retry.
        return;
      }
    }

    const cap = this.queuedTierCap.cap;
    const pause = computeClientsToPause(this.clientSlots, cap);
    this.setQueuedPauseNotice(pause && {
      count: pause.count,
      names: pause.names,
      cap: cap!,
      date: describeQueuedTierChangeDate(tier, BOOT_LOCALE),
    });
  }

  /**
   * The client limit the coach's plan changes to at their next renewal, and the
   * plan identifier it was resolved FOR.
   *
   * **Keyed by that identifier, and that is the whole point.** Resolving it is a
   * network read, so it is worth keeping — but this component instance survives
   * leaving and re-entering the page, and a coach can queue a DIFFERENT plan
   * change on the billing screen and come straight back. A cap remembered from
   * the previous queued plan would then name the wrong clients in the notice,
   * which is worse than showing no notice at all.
   *
   * `cap` is undefined when the catalogue holds no entry for that identifier —
   * a real answer meaning "say nothing", not a missing one.
   */
  private queuedTierCap?: { productId: string; cap: number | undefined };

  private setQueuedPauseNotice(notice: typeof this.queuedPauseNotice): void {
    this.queuedPauseNotice = notice ?? null;
    this.cd.markForCheck();
  }

  /** The notice text, chosen by whether the renewal date is known. */
  queuedPauseNoticeText(notice: NonNullable<typeof this.queuedPauseNotice>): TranslatedString {
    return notice.date
      ? LL.coach.tier.pauseScheduledNotice.body({
        cap: notice.cap, count: notice.count, names: notice.names, date: notice.date,
      })
      : LL.coach.tier.pauseScheduledNotice.bodyNoDate({
        cap: notice.cap, count: notice.count, names: notice.names,
      });
  }

  getStatusLabel(slot: CoachClientSlot): TranslatedString {
    return slot.status === CoachClientStatus.Claimed
      ? LL.coach.status.claimed()
      : LL.coach.status.pending();
  }

  getStatusColor(slot: CoachClientSlot): string {
    return slot.status === CoachClientStatus.Claimed
      ? BadgeColor.Green
      : BadgeColor.Yellow;
  }

  // ── dashboard view-model accessors used by the template ──

  /** Claimed slots only — these render in the dashboard table (sorted per R5). */
  get claimedSlots(): CoachClientSlot[] {
    const claimed = this.clientSlots.filter(s => s.status === CoachClientStatus.Claimed && !!s.clientUid);
    // join slots with their dashboard rows for sorting
    const annotated = claimed.map(slot => ({
      slot,
      row: this.dashboardByClientUid.get(slot.clientUid) ?? { clientUid: slot.clientUid, status: 'Unknown' as const, items: [] },
    }));
    const sortedRows = sortDashboardRows(annotated.map(a => a.row));
    const order = new Map(sortedRows.map((r, i) => [r.clientUid, i]));
    return annotated
      .sort((a, b) => (order.get(a.slot.clientUid) ?? 0) - (order.get(b.slot.clientUid) ?? 0))
      .map(a => a.slot);
  }

  /** Pending invitations render in a separate section below the dashboard. */
  get pendingSlots(): CoachClientSlot[] {
    return this.clientSlots.filter(s => s.status === CoachClientStatus.Pending);
  }

  /** Row for a slot, or undefined while the per-client fetch is still in flight (skeleton). */
  getDashboardRow(slot: CoachClientSlot): CoachDashboardRow | undefined {
    if (!slot.clientUid) return undefined;
    return this.dashboardByClientUid.get(slot.clientUid);
  }

  isDashboardLoading(slot: CoachClientSlot): boolean {
    // A PARKED client is never "loading" (#4530). Nothing is being fetched for
    // them — `claimedClientUids` deliberately leaves them out — so their row
    // would otherwise sit on a skeleton placeholder for ever, because the
    // absence of a dashboard entry is what "still loading" is inferred from.
    // Answering here rather than adding a branch per cell means any cell that
    // asks this question gets the right answer, including one added later.
    if (isSlotSleeping(slot)) return false;
    return slot.status === CoachClientStatus.Claimed && !this.dashboardByClientUid.has(slot.clientUid || '');
  }

  /** Label for the status badge ("On track" / "Off track" / "Undetermined" / "Unknown").
   *  Record type guarantees every key has an entry — no runtime fallback needed. */
  getDashboardStatusLabel(row: CoachDashboardRow | undefined): TranslatedString {
    return STATUS_LABEL_FN[row?.status ?? 'Unknown']();
  }

  /**
   * Whether to warn the coach that this client's account never received the
   * handover (#4817).
   *
   * A parked client is excluded here as well as inside `checkClientHandover`,
   * for the same reason their on-track status is hidden: while they are asleep
   * the coach is not coaching them, so nothing about their program is worth
   * putting in front of the coach.
   */
  hasBrokenHandover(slot: CoachClientSlot): boolean {
    if (isSlotSleeping(slot)) return false;
    return this.getDashboardRow(slot)?.handover === HandoverState.Missing;
  }

  /** The loudest badge colour this app has. There is no red one, and adding one
   *  is a theme change this ticket has no business making. */
  readonly brokenHandoverBadgeColor = BadgeColor.Magenta;

  /** Badge color for the status indicator (mirrors the pending-row badge family). */
  getDashboardStatusColor(row: CoachDashboardRow | undefined): BadgeColor {
    return STATUS_BADGE_COLOR[row?.status ?? 'Unknown'];
  }

  /** Display string for the action-items cell ("—" when zero, otherwise the count).
   *  Informational only; tapping the row opens the client detail page where the
   *  individual items are surfaced. */
  getItemsCellLabel(row: CoachDashboardRow | undefined): string {
    if (!row || !row.items?.length) return LL.coach.dashboard.itemsNone();
    return String(row.items.length);
  }

  /** Manual refresh — re-runs the dashboard fetch for all claimed clients. */
  async refreshDashboardTapped() {
    if (this.isRefreshing) return;
    await this.refreshDashboardAsync();
  }

  async addClient() {
    const nickname = await promptCoachNicknameAsync(
      this.alertCtrl, this.clientSlots, { mode: 'create' },
    );
    if (!nickname) return;

    const slot: CoachClientSlot = {
      _id: `coach-client/${crypto.randomUUID()}`,
      nickname,
      status: CoachClientStatus.Pending,
      createdAt: new Date().toISOString(),
    };
    await this.userDb.database.saveRecordAsync(slot, DatabaseWrapper.getTransactionId());
    void this.navCtrl.pushAsync(ICoachClientDetailPage, {
      clientSlotId: slot._id,
    });
  }

  openClientDetail(slot: CoachClientSlot) {
    void this.navCtrl.pushAsync(ICoachClientDetailPage, {
      clientSlotId: slot._id,
    });
  }

  /** Whether this client's card offers the Training Overview button (#6795); the rule is in `training-overview-entry.ts`. */
  showsTrainingOverview(slot: CoachClientSlot): boolean {
    return isTrainingOverviewEntryShown(slot, {
      coachingDashboardOn: this.coachingDashboardOn,
      isDemoClient: !!slot.clientUid && this.demoSeed.isSynthetic(slot.clientUid),
    });
  }

  /** Enter this client's account and open their Training Overview there (#6795). The card around
   *  the button opens the detail page, so the tap must not reach it. */
  openTrainingOverview(event: Event, slot: CoachClientSlot) {
    event.stopPropagation();
    lockClick(event, this.clientEntry.enterClientAccountAsync(slot, 'training-overview'));
  }

  /** Navigate to the Coach Billing dashboard (#3666 Phase 1). The
   *  page itself handles platform branching (PayPro vs iOS placeholder),
   *  unmigrated coaches, and load-failure copy. */
  openBilling() {
    void this.navCtrl.pushAsync(ICoachBillingPage, {});
  }

  /**
   * Whether the coach can use Tier 1 white-labeling (#3707). Ungated in v1 —
   * any coach (effective slots > 0, incl. an app-store tier) can brand. Routing
   * the header icon's visibility through this one getter means a future
   * billing-tier gate can be added here without touching the template or other
   * call sites.
   */
  canUseBranding(): boolean {
    // read live (not the cached `maxSlots`, which is set once in
    // ionViewWillEnter): coach recognition is transiently false while auth's
    // user info reloads after an app reload, which would otherwise hide the
    // branding icon until the page is re-entered. Reading live lets it reappear
    // on the next change-detection pass once auth has loaded. `getIsCoach()`
    // also recognizes a Paddle per-seat coach (uncapped, effectiveCoachSlots 0).
    return this.auth.getIsCoach();
  }

  /** Navigate to the Coach Branding setup page (#3707). */
  openBranding() {
    void this.navCtrl.pushAsync(ICoachBrandingPage, {});
  }
}
